/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Recovery } from 'models/repossession/interfaces/recovery.interface';
import { Button, Input, Typography } from 'design_system/src';
import { Map, Marker, GeolocateControl, MapRef } from 'react-map-gl';
import mapboxgl from 'utils/mapboxgl';
import { LngLatBounds } from 'mapbox-gl';

import { momentLocal } from 'models/shared/helpers/dates';
import {
	InterestPointType,
	InterestPointTypeValues,
	RepossessionEvidenceType,
	RepossessionPolicy,
} from 'models/repossession/enums/repossession.enums';
import { findOneLoan } from 'models/loan/controllers/loan.controller';
import { LoanWithExtraFields } from 'models/loan/interfaces/loan.interface';
import { EvidenceDocumentType } from 'models/shared/types/documents';
import { priceFormatFinancial } from 'models/shared/helpers/prices';
import profileDefaultOzoner from 'static/images/default_profile_ozoner.png';
import { ReactComponent as Moto } from 'design_system/src/static/icons/motorcycle.svg';

import { ReactComponent as Resolution } from 'design_system/src/static/icons/resolution.svg';
import { ReactComponent as Phone } from 'design_system/src/static/icons/calling.svg';
import { ReactComponent as Note } from 'design_system/src/static/icons/note.svg';
import { ReactComponent as Whatsapp } from 'design_system/src/static/icons/whatsapp.svg';
import { ReactComponent as Location } from 'design_system/src/static/icons/gps.svg';
import { ReactComponent as Down } from 'design_system/src/static/icons/down-sm.svg';
import { ReactComponent as Close } from 'design_system/src/static/icons/close-lg.svg';

import {
	AddressRecoveryFinalConditions,
	RecoveryCanceledConditions,
	RecoveryFinalConditions,
	RecoveryFinalConditionsValues,
	RecoveryStatus,
} from 'models/repossession/enums/recovery.enums';
import {
	updateRecovery,
	uploadFileRepossession,
} from 'models/repossession/controllers/repossession.controller';
import { toasterNotification } from 'models/shared/helpers/toasterNotification';
import { toasterOptions } from 'models/shared/enums/toaster.enum';
import { commonErrorHandler } from 'models/shared/helpers/errors';
import {
	fetchCurrentTile,
	fetchTileLocation,
} from 'models/geozon/controllers/tile.controller';
import { Coordinate } from 'models/shared/types/basic';
import { repossessionMobileContext } from './RepossessionsMobileContext';
import LoadRecoveryEvidence from './loadRecoveryEvidence/loadRecoveryEvidence';

// eslint-disable-next-line no-shadow
enum finalOptionsEnum {
	Recovered = 'Recovered',
	NotRecovered = 'NotRecovered',
}

let intervalId: ReturnType<typeof setInterval>;

export const RouteDetailMobile = () => {
	const mapRef = useRef<MapRef>(null);

	const { _id } = useParams();
	const navigate = useNavigate();
	const { recoveries, updateRecoveries, initializeContext, gpsLocations } =
		useContext(repossessionMobileContext);
	const [viewState, setViewState] = useState({
		longitude: -99.12766,
		latitude: 19.42847,
		zoom: 10,
	});

	const [currentRecovery, setCurrentRecovery] = useState<Recovery>();
	const [recoveryIndex, setRecoveryIndex] = useState(0);
	const [loan, setLoan] = useState<LoanWithExtraFields>();
	const [open, setOpen] = useState(true);
	const [finalOption, setFinalOption] = useState<finalOptionsEnum>();

	const [finalCondition, setFinalCondition] = useState();
	const [recoveryComment, setRecoveryComment] = useState('');
	const [image, setImage] = useState<File>();
	const [loading, setLoading] = useState(false);
	const [tileLocation, setTileLocation] = useState<Coordinate>();

	useEffect(
		() => () => {
			if (intervalId) {
				clearInterval(intervalId);
			}
		},
		[],
	);

	useEffect(() => {
		const index = recoveries.findIndex((recovery) => recovery._id === _id);
		setCurrentRecovery(recoveries[index]);
		setRecoveryIndex(index);
		setOpen(true);
	}, [_id, recoveries]);

	const openLocation = () => {
		const coordinates = currentRecovery?.interestPoint?.coordinates;
		if (!coordinates) return;
		window.open(
			`https://www.google.com/maps/search/?api=1&query=${coordinates.lat},${coordinates.long}`,
			'_blank',
		);
	};

	const goWA = () => {
		const number =
			currentRecovery?.repossession.loan.financialForm.personalInformation
				?.phone;
		if (!number) return;
		window.open(`https://api.whatsapp.com/send?phone=${number}`, '_blank');
	};

	const openPhone = () => {
		const number =
			currentRecovery?.repossession.loan.financialForm.personalInformation
				?.phone;
		if (!number) return;
		window.open(`tel:${number}`, '_self');
	};

	const goToVehicle = () => {
		if (!currentRecovery?.interestPoint) return;

		let location = currentRecovery.interestPoint.coordinates;
		if (
			[
				InterestPointType.signalVehicle,
				InterestPointType.noSignalVehicle,
			].includes(currentRecovery.interestPoint.type) &&
			gpsLocations[currentRecovery._id]
		) {
			location = gpsLocations[currentRecovery._id];
		}
		if (location.long && location.lat) {
			mapRef?.current?.flyTo({
				center: {
					lat: location.lat - 0.004,
					lng: location.long,
				},
				zoom: 15,
			});
		}
	};

	const centerRecovery = () => {
		if (
			navigator.geolocation &&
			currentRecovery?.interestPoint.coordinates.long &&
			currentRecovery?.interestPoint.coordinates.lat
		) {
			navigator.geolocation.getCurrentPosition((position) => {
				const bounds = new LngLatBounds();
				bounds.extend([position.coords.longitude, position.coords.latitude]);
				bounds.extend([
					currentRecovery.interestPoint.coordinates.long,
					currentRecovery.interestPoint.coordinates.lat,
				]);
				mapRef.current?.fitBounds(bounds, { padding: 100 });
			});
		}
	};

	useEffect(() => {
		if (!currentRecovery) return;
		findOneLoan(currentRecovery.repossession.loan._id).then((resp) => {
			setLoan(resp);
		});
		if (
			currentRecovery.interestPoint.type ===
				InterestPointType.noSignalTileVehicle ||
			currentRecovery.interestPoint.type === InterestPointType.signalTileVehicle
		) {
			fetchCurrentTile(currentRecovery.repossession.loan.vehicle.internalId)
				.then((tile) => {
					if (tile.device) {
						if (intervalId) {
							clearInterval(intervalId);
						}
						intervalId = setInterval(() => {
							fetchTileLocation(tile.device._id).then((t) => {
								if (t?.location) {
									setTileLocation({
										lat: t.location.latitude,
										long: t.location.longitude,
									});
								}
							});
						}, 30000);
						return fetchTileLocation(tile.device._id);
					}
					return Promise.resolve(null);
				})
				.then((tile) => {
					if (tile?.location) {
						setTileLocation({
							lat: tile.location.latitude,
							long: tile.location.longitude,
						});
					}
				});
		}
	}, [currentRecovery]);

	const markers = useMemo(() => {
		if (!currentRecovery?.interestPoint) return [];
		const Icon =
			InterestPointTypeValues[currentRecovery.interestPoint.type].icon;

		let location = currentRecovery.interestPoint.coordinates;
		if (
			[
				InterestPointType.signalVehicle,
				InterestPointType.noSignalVehicle,
			].includes(currentRecovery.interestPoint.type) &&
			gpsLocations[currentRecovery._id]
		) {
			location = gpsLocations[currentRecovery._id];
		}
		if (
			[
				InterestPointType.signalTileVehicle,
				InterestPointType.noSignalTileVehicle,
			].includes(currentRecovery.interestPoint.type) &&
			tileLocation
		) {
			location = tileLocation;
		}
		goToVehicle();

		return [
			<Marker
				key={currentRecovery._id}
				longitude={location.long}
				latitude={location.lat}
			>
				<div
					className={`br_xxl main_marker ${
						InterestPointTypeValues[currentRecovery.interestPoint.type].color
					} `}
				>
					<div className="marker_info p_y_xxs p_x_md flex_center flex_gap_sm">
						<Icon className="dim_lg icon" />
						<Typography weight="600" scale="xsmall">
							{currentRecovery.repossession.loan.vehicle.internalId}
						</Typography>
					</div>
					<div className="marker_details p_x_md flex_center_col">
						<Typography weight="600" scale="xxsmall">
							{momentLocal(currentRecovery?.interestPoint?.eventDate).format(
								'DD/MM/YYYY',
							)}
						</Typography>
						<Typography weight="400" scale="xxsmall">
							{momentLocal(currentRecovery?.interestPoint?.eventDate).format(
								'HH:mm',
							)}
						</Typography>
					</div>
					<span className="arrow" />
				</div>
			</Marker>,
		];
	}, [currentRecovery, gpsLocations, tileLocation]);

	useEffect(() => {
		if (finalCondition === RecoveryFinalConditions.paidOnSite) {
			setRecoveryComment(
				`Se realizo el pago de la deuda por un valor de: ${loan?.lateAmount}`,
			);
		}
	}, [finalCondition]);

	const failRecovery = () => {
		if (currentRecovery && finalCondition && recoveryComment) {
			const status = RecoveryCanceledConditions.includes(finalCondition)
				? RecoveryStatus.canceled
				: RecoveryStatus.failed;
			setLoading(true);
			updateRecovery(currentRecovery._id, {
				status,
				finalCondition,
				comment: recoveryComment,
			})
				.then((recovery) => {
					updateRecoveries(recovery);
					toasterNotification({
						msg: 'Recovery actualizada correctamente',
						style: 'light',
						toasterType: toasterOptions.success,
					});
					if (status === RecoveryStatus.canceled) {
						initializeContext();
					}
					setLoading(false);
					navigate('/routes');
				})
				.catch((e) => {
					setLoading(false);
					commonErrorHandler(e);
				});
		}
	};
	const completeRecovery = () => {
		if (currentRecovery && image) {
			setLoading(true);

			const blob = image.slice(0, image.size, image.type);
			const newFile = new File(
				[blob],
				`${currentRecovery._id}_${RepossessionEvidenceType.recoveryOnSite}`,
				{
					type: image.type,
				},
			);
			uploadFileRepossession(
				currentRecovery.repossession._id,
				newFile,
				RepossessionEvidenceType.recoveryOnSite,
			)
				.then(() => {
					toasterNotification({
						msg: 'Documento cargado satisfactoriamente',
						style: 'light',
						toasterType: toasterOptions.success,
					});
					return updateRecovery(currentRecovery._id, {
						status: RecoveryStatus.recovered,
						finalCondition:
							currentRecovery.repossession.policy ===
							RepossessionPolicy.devolution
								? RecoveryFinalConditions.devolution
								: RecoveryFinalConditions.recovered,
						comment: 'Vehículo recuperado',
					});
				})
				.then((recovery) => {
					updateRecoveries(recovery);
					toasterNotification({
						msg: 'Recovery actualizada correctamente',
						style: 'light',
						toasterType: toasterOptions.success,
					});
					initializeContext();
					setLoading(false);
					navigate('/routes');
				})
				.catch((e) => {
					setLoading(false);
					commonErrorHandler(e);
				});
		}
	};

	return currentRecovery ? (
		<div className="pos_absolute dim_100_per map_mobile_cont overflow_hidden">
			<div className="recovery_title">
				<div className="p_y_xs p_x_md bg_neutral_0 br_xs shadow_hard w_fit m_b_xs">
					<Typography weight="600" scale="heading2">
						{currentRecovery.repossession.loan.vehicle.internalId}
					</Typography>
				</div>
				<div className="p_y_xs p_x_md bg_neutral_0 br_xs shadow_hard w_fit">
					<div className="display_flex flex_align_center flex_gap_xs ">
						{(() => {
							const type =
								InterestPointTypeValues[currentRecovery.interestPoint.type];
							return (
								<>
									<type.icon className={`dim_md text_${type.realColor}`} />
									<Typography className="" scale="large" weight="400">
										{type.title}
									</Typography>
								</>
							);
						})()}
					</div>
				</div>
			</div>

			<div className="recovery_counter">
				<div className="p_y_xs p_x_md bg_neutral_0 br_xs shadow_hard w_fit">
					<Typography weight="400" scale="medium">
						{(recoveryIndex + 1).toString().padStart(2, '0')}/
						{recoveries.length.toString().padStart(2, '0')}
					</Typography>
				</div>
			</div>
			<div
				className={`recovery_details p_y_lg p_x_xs bg_neutral_0 br_xs shadow_hard w_95_per ${
					open ? 'open' : ''
				}`}
			>
				<div className="display_flex flex_justify_strech flex_gap_xs">
					<div className="br_md shadow_hard flex_grow_1 flex_basis_0 overflow_hidden">
						<div className="border_4 border_solid border_primary_300 recovery_photo_container br_md overflow_hidden w_100_per">
							<img
								src={
									currentRecovery.repossession.loan.financialForm.ozoner.documents?.find(
										(doc) => doc.typeEvidence === EvidenceDocumentType.selfie,
									)?.url ?? profileDefaultOzoner
								}
								alt=""
								className="img_cover"
							/>
						</div>
						<div className="p_md display_flex flex_col">
							<Typography scale="large" weight="600">
								{currentRecovery.repossession.loan.financialForm.ozoner
									.firstName ?? ''}{' '}
								{currentRecovery.repossession.loan.financialForm.ozoner
									.middleName ?? ''}{' '}
								{currentRecovery.repossession.loan.financialForm.ozoner
									.lastName ?? ''}
							</Typography>
							<Typography scale="xsmall" weight="400">
								CURP:{' '}
								{currentRecovery.repossession.loan.financialForm.ozoner.curp}
							</Typography>
							<Typography scale="xsmall" weight="400">
								{currentRecovery.repossession.loan.financialForm.ozoner.email}
							</Typography>
						</div>
					</div>
					<div className="br_md shadow_hard flex_grow_1 flex_basis_0 overflow_hidden">
						<div className="border_4 border_solid border_primary_300 recovery_photo_container br_md overflow_hidden w_100_per">
							<img
								src={
									currentRecovery.repossession.loan.vehicle.images?.[0]?.url ??
									''
								}
								alt=""
								className="img_cover"
							/>
						</div>
						<div className="p_md flex_center_col flex_align_start">
							<Typography scale="medium" weight="600">
								{currentRecovery.repossession?.loan.vehicle?.brand?.name || '-'}{' '}
								{currentRecovery.repossession?.loan.vehicle?.brand?.models?.find(
									(model) =>
										model._id ===
										(currentRecovery.repossession?.loan.vehicle?.model as any),
								)?.name || '-'}{' '}
								{currentRecovery.repossession?.loan.vehicle
									?.cylindersCapacity || '-'}{' '}
								{currentRecovery.repossession?.loan.vehicle?.year || '-'}
							</Typography>
							<Typography scale="xsmall" weight="400">
								serie: {currentRecovery.repossession?.loan.vehicle.vehicleSN}
							</Typography>
						</div>
					</div>
				</div>
				<div className="br_md shadow_hard flex_grow_1 flex_basis_0 overflow_hidden m_y_md">
					<div className="border_b_1 border_b_solid border_primary_100 flex_center p_y_sm">
						<Typography scale="medium" weight="400" textColor="neutral_700">
							<Typography
								scale="medium"
								weight="600"
								component="span"
								textColor="neutral_700"
							>
								{loan?.lateQuotas || 0}
							</Typography>{' '}
							cuotas en mora
						</Typography>
					</div>
					<div className="border_b_1 border_b_solid border_primary_100 flex_center p_y_sm">
						<Typography scale="medium" weight="400" textColor="neutral_700">
							<Typography
								scale="medium"
								weight="600"
								component="span"
								textColor="green_300"
							>
								{loan?.paidQuotas || 0}/{loan?.duration || 0}
							</Typography>{' '}
							cuotas pagadas
						</Typography>
					</div>
					<div className=" flex_center p_y_sm">
						<Typography scale="medium" weight="400" textColor="neutral_700">
							<Typography
								scale="medium"
								weight="600"
								component="span"
								textColor="neutral_700"
							>
								{priceFormatFinancial(loan?.lateAmount || 0)}
							</Typography>{' '}
							para regularizar
						</Typography>
					</div>
				</div>
				<div className="contact_options border_3 br_xxl border_solid border_primary_300 w_70_per flex_center flex_gap_xl p_y_xs p_x_md bg_neutral_0">
					<Phone
						className="dim_xxl text_primary_300 cursor_pointer border_transparent"
						onClick={openPhone}
					/>
					<Whatsapp
						className="dim_xxl text_primary_300 cursor_pointer border_transparent"
						onClick={goWA}
					/>
					<Location
						className="dim_xxl text_primary_300 cursor_pointer border_transparent"
						onClick={openLocation}
					/>
				</div>
				<div
					className="close_icon_cont border_3 br_circle cursor_pointer border_solid border_primary_300 flex_center p_xxs bg_neutral_0"
					onClick={() => setOpen(!open)}
					tabIndex={0}
					role="button"
					onKeyDown={() => setOpen(!open)}
				>
					<Down className="close_icon dim_xxl text_primary_300  border_transparent" />
				</div>
			</div>
			<div
				className={`next_action_recovery display_flex flex_gap_md ${
					open ? 'open' : ''
				}`}
			>
				<Button
					scale="small"
					color="red"
					className="flex_1"
					onClick={() => setFinalOption(finalOptionsEnum.NotRecovered)}
				>
					No recuperada
				</Button>
				<Button
					scale="small"
					className="flex_1"
					onClick={() => setFinalOption(finalOptionsEnum.Recovered)}
				>
					Recuperada
				</Button>
			</div>
			<div
				className={`recovery_final_form bg_neutral_0 ${
					finalOption ? 'open' : ''
				}`}
			>
				<Close
					className="close_icon dim_xl cursor_pointer"
					onClick={() => setFinalOption(undefined)}
				/>
				{finalOption === finalOptionsEnum.NotRecovered ? (
					<div className="flex_content_col">
						<div className="flex_body p_x_md">
							<Input
								type="select"
								name="finalCondition"
								title="Comentario final"
								value={finalCondition}
								icon={<Note />}
								placeholder="Selecciona una opción"
								onChange={(e: any) => setFinalCondition(e.target.value)}
								options={Object.entries(RecoveryFinalConditionsValues)
									.filter(
										([option, value]) =>
											!value.hidden ||
											(currentRecovery.interestPoint.type ===
												InterestPointType.address &&
												AddressRecoveryFinalConditions.includes(
													option as RecoveryFinalConditions,
												)),
									)
									.map(([option, value]) => ({
										label: value.title,
										value: option,
									}))}
							/>
							<Input
								type="textarea"
								name="comment"
								title="Agregar nota"
								placeholder="Escribe una nota sobre el recovery"
								inputClassName="h_10_vp"
								icon={<Note />}
								value={recoveryComment}
								onChange={(e: any) => setRecoveryComment(e.target.value)}
							/>
						</div>
						<div className="flex_footer p_md">
							<Button
								scale="small"
								color="red"
								className="w_100_per"
								disabled={!finalCondition || !recoveryComment || loading}
								onClick={() => failRecovery()}
							>
								Marcar como no recuperada
							</Button>
						</div>
					</div>
				) : (
					<div className="flex_content_col">
						<div className="flex_body p_x_md p_t_md">
							<LoadRecoveryEvidence
								image={image}
								setImage={setImage}
								placeholder="Añadir foto de la moto recuperada"
							/>
						</div>
						<div className="flex_footer p_md">
							<Button
								scale="small"
								className="w_100_per"
								disabled={loading || !image}
								onClick={() => completeRecovery()}
							>
								Marcar como recuperada
							</Button>
						</div>
					</div>
				)}
			</div>
			<div
				className={`backdrop ${!open ? 'close' : ''}`}
				onClick={() => setOpen(false)}
				onKeyDown={() => setOpen(false)}
				tabIndex={0}
				role="link"
			/>
			<Map
				mapLib={mapboxgl}
				mapboxAccessToken={process.env.REACT_APP_MAPBOX_API_KEY || ''}
				onMove={(evt) => setViewState(evt.viewState)}
				mapStyle="mapbox://styles/mapbox/streets-v9"
				ref={mapRef}
				{...viewState}
			>
				<GeolocateControl />
				<div className="map_controls display_flex flex_col">
					<div className="mapboxgl-ctrl-group ">
						<button
							type="button"
							className="flex_center dim_100"
							onClick={centerRecovery}
						>
							<Resolution className="dim_80_per text_neutral_900 cursor_pointer" />
						</button>
					</div>
					<div className="mapboxgl-ctrl-group ">
						<button
							type="button"
							className="flex_center dim_100"
							onClick={goToVehicle}
						>
							<Moto className="dim_80_per text_neutral_900 cursor_pointer" />
						</button>
					</div>
				</div>
				{markers}
			</Map>
		</div>
	) : null;
};
